import React from 'react';
import { PointEntity } from '@src/features/order/entities';
import { ArcSocketContext } from '@src/core/socket';
import { useFlightProvider } from '@src/features/order/provider';
import { useMatch, useSearchParams } from 'react-router-dom';
import { routesConfig } from '@src/core/routes';
import { useGoHereClearance } from '@src/features/drone/hooks/useGoHereClearance';
import {
  registerGoHereReset,
  unregisterGoHereReset,
} from '@src/features/drone/utils/go-here-registry';

const useGoTo = () => {
  const [searchParams] = useSearchParams();
  const match = useMatch(`${routesConfig.orders}/take-off-to-en-route/:missionId`);

  const { arcSocket } = React.useContext(ArcSocketContext);
  const { flightData } = useFlightProvider();
  const [points, setPoints] = React.useState<PointEntity[]>([]);
  const [position, setPosition] = React.useState<google.maps.LatLngLiteral | null>(null);

  const removePosition = React.useCallback(() => setPosition(null), []);

  const addPosition = React.useCallback(async (e: google.maps.MapMouseEvent) => {
    const lat = e.latLng?.lat();
    const lng = e.latLng?.lng();

    if (lat && lng) setPosition({ lat, lng });
  }, []);

  const resetGoHere = React.useCallback(() => {
    setPoints([]);
    setPosition(null);
  }, []);

  const droneSerial =
    searchParams.get('droneSerial') ??
    (flightData?.serialNumber as string | undefined) ??
    null;

  useGoHereClearance({ resetGoHere, points, serialNumber: droneSerial });

  React.useEffect(() => {
    if (!droneSerial) return;
    registerGoHereReset(droneSerial, resetGoHere);
    return () => unregisterGoHereReset(droneSerial);
  }, [droneSerial, resetGoHere]);

  const goTo = React.useCallback((e: google.maps.LatLngLiteral) => {
    const socket = arcSocket();
    const serialNumber = searchParams.get('droneSerial');
    
    if (!socket || !match) return;

    const point: PointEntity = {
      id: Date.now(),
      position: { 
        lat: e.lat, 
        lng: e.lng, 
      }
    };
    setPoints([point]); // replaces any previous Go Here target
    socket.send(
      JSON.stringify({
        serialNumber,
        type: "COMMAND",
        command: "GOTO",
        latitude_deg: e.lat,
        longitude_deg: e.lng,
      })
    );
  }, [arcSocket, match, searchParams]);
 
  return {
    goTo,
    points, 
    position,
    resetGoHere,
    addPosition,
    removePosition,
  };
};

export default useGoTo;